import { useEffect, useRef } from 'react';
import { desktopBridge } from '../bridge';
import type { DesktopEvent, DesktopEventType } from '../types';

/**
 * Subscribes to ONE kind of main-process event.
 *
 * The handler is held in a ref, so callers do not have to memoise it and the
 * IPC listener is attached exactly once for the life of the component — which
 * matters because these fire while the window is hidden and a churning
 * listener would drop events between renders.
 */
export function useDesktopEvent<T extends DesktopEventType>(
  type: T,
  handler: (event: Extract<DesktopEvent, { type: T }>) => void,
): void {
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    const bridge = desktopBridge();
    if (bridge === null) return undefined;
    return bridge.onEvent((event) => {
      if (event.type !== type) return;
      latest.current(event as Extract<DesktopEvent, { type: T }>);
    });
  }, [type]);
}
